import { Flex, FlexProps, Spinner, Text, TextProps } from "@chakra-ui/react";
import NumberFlow from "@number-flow/react";
import React from "react";

import { unavailableRolloutCountText } from "@/common/components/AudienceCountUnavailable";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import { useTargetingEstimation } from "@/common/data/useTargetingEstimation";
import { FeatureTargeting } from "@reflag/shared/featureTargetingAPI";
import pluralize from "@reflag/shared/utils/pluralize";

const sharedEstimationTextProps = {
  as: "span",
  color: "dimmed",
  fontSize: "sm",
  lineHeight: "21px",
} satisfies TextProps;

const rulesRemovalText =
  "Company count includes all companies with at least one user targeted. User count includes all users in targeted companies and any additional users targeted separately.";
type Props = FlexProps & {
  targeting: FeatureTargeting;
  flagKey: string;
  optInMemberships?: { userIds: string[]; companyIds: string[] };
};

export const RulesEstimationSingle = ({
  targeting,
  flagKey,
  optInMemberships,
  ...rest
}: Props) => {
  const {
    data: estimate,
    isLoading: isLoadingEstimate,
    isFetching: isFetchingEstimate,
    isSuccess: isSuccessEstimate,
  } = useTargetingEstimation(targeting, flagKey, true, optInMemberships);

  if (isLoadingEstimate) {
    return (
      <Flex align="center" {...rest}>
        <Spinner size="xs" variant="subtle" />
      </Flex>
    );
  }

  if (isSuccessEstimate && estimate) {
    const { userCount, companyCount } = estimate;
    const hasUnavailableCount = userCount === null || companyCount === null;
    const estimationText = hasUnavailableCount ? (
      "Audience count unavailable"
    ) : (
      <>
        <NumberFlow
          format={{ notation: "compact" }}
          value={userCount}
          isolate
        />{" "}
        {pluralize("known user", userCount)}
        {" in "}
        <NumberFlow
          format={{ notation: "compact" }}
          value={companyCount}
          isolate
        />{" "}
        {pluralize("company", companyCount)} have access
      </>
    );

    return (
      <Flex {...rest}>
        <Flex align="center" direction="row" gap={1}>
          <Text {...sharedEstimationTextProps}>{estimationText}</Text>
          {isFetchingEstimate ? (
            <Spinner size="xs" variant="subtle" />
          ) : (
            <InfoIconTooltip
              renderInPortal={false}
              text={
                hasUnavailableCount
                  ? unavailableRolloutCountText
                  : rulesRemovalText
              }
            />
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex {...rest}>
      <Text {...sharedEstimationTextProps}>0 companies</Text>
    </Flex>
  );
};
